import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { NotificationTypeEnum } from '@/types';
import { LocationModel, MinimizedLocation } from '@/types/location';
import { useDebounce } from '@/hooks/useDebounce';
import { locationProvider } from '@/providers/locationProvider';
import { appSetNotification } from '@/store';
import { createLatestRequestGuard } from './latestRequestGuard';

interface UseLocationAutocompleteProps {
  defaultValue: string;
  searchByDistrict?: boolean;
  searchWithProperty?: boolean;
  skipInitialSearch?: boolean;
}

export const useLocationAutocomplete = ({
  defaultValue,
  searchByDistrict,
  searchWithProperty,
  skipInitialSearch = false,
}: UseLocationAutocompleteProps) => {
  const dispatch = useDispatch();
  const formContext = useFormContext();
  const propertyKind = formContext ? formContext.watch('propertyKind') : null;
  const [locations, setLocations] = useState<LocationModel[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationModel | null>(null);
  const [minimizedLocations, setMinimizedLocations] = useState<MinimizedLocation[]>([]);
  const [inputAddressValue, setInputAddressValueState] = useState<string>(defaultValue);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const inputAddressValueRef = useRef(defaultValue);
  const hasSearchInteractionRef = useRef(!skipInitialSearch);
  const requestGuardRef = useRef(createLatestRequestGuard());
  const requestGuard = requestGuardRef.current;
  const setInputAddressValue = useCallback(
    (value: string) => {
      hasSearchInteractionRef.current = true;
      if (inputAddressValueRef.current === value) return;

      inputAddressValueRef.current = value;
      requestGuard.invalidate();
      setIsLoadingLocations(false);
      setLocations([]);
      setInputAddressValueState(value);
    },
    [requestGuard]
  );
  const searchValue = useDebounce(inputAddressValue);

  useEffect(() => {
    if (!hasSearchInteractionRef.current) return;
    if (searchValue !== inputAddressValueRef.current) return;

    if (!searchValue) {
      setIsLoadingLocations(false);
      setLocations([]);
      if (searchByDistrict) setSelectedLocation(null);
      return;
    }

    const requestId = requestGuard.start();
    const loadLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const response = searchByDistrict
          ? await locationProvider.getAds({ name: searchValue, searchByDistrict })
          : searchWithProperty
            ? await locationProvider.getAds({ name: searchValue, propertyKind })
            : await locationProvider.getAds({ name: searchValue });

        if (!requestGuard.isLatest(requestId)) return;

        if (!response.ok) {
          setLocations([]);
          dispatch(
            appSetNotification(
              NotificationTypeEnum.Error,
              response.message || 'Failed to search locations',
              response.status
            )
          );
          return;
        }

        const items = response.data?.items ?? [];
        setLocations(items);
        if (searchByDistrict) setSelectedLocation(items[0] ?? null);
      } catch (error) {
        if (!requestGuard.isLatest(requestId)) return;

        const message = error instanceof Error ? error.message : 'Failed to search locations';
        setLocations([]);
        dispatch(appSetNotification(NotificationTypeEnum.Error, message));
      } finally {
        if (requestGuard.isLatest(requestId)) {
          setIsLoadingLocations(false);
        }
      }
    };

    void loadLocations();

    return () => {
      if (requestGuard.isLatest(requestId)) requestGuard.invalidate();
    };
  }, [dispatch, propertyKind, requestGuard, searchByDistrict, searchValue, searchWithProperty]);

  return {
    locations,
    setLocations,
    inputAddressValue,
    setInputAddressValue,
    isLoadingLocations,
    selectedLocation,
    setSelectedLocation,
    minimizedLocations,
    setMinimizedLocations,
  };
};
